import React, { useEffect, useState, useContext, useRef } from 'react';
import { Container, Typography, Box, Stack, TextField, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useParams } from 'react-router-dom';
import chatApi from '../../api/chatApi';
import { AuthContext } from '../../auth/AuthContext';
import BackButtonDesktop from '../../components/BackButtonDesktop';

export default function ChatRoomPage() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const pageSize = 20;
  const containerRef = useRef();

  // --- Carica stanza e messaggi iniziali ---
  useEffect(() => {
    fetchRoom();
  }, [id]);

  const fetchRoom = async () => {
    try {
      setLoading(true);
      const res = await chatApi.get(id);
      setRoom(res.data);
      setMessages([]);
      setPage(0);
      setHasMore(true);
      fetchMessages(0, true);
    } catch (err) {
      console.error('Errore caricamento stanza', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (pageToLoad = 0, scrollToBottom = false) => {
    try {
      const res = await chatApi.getMessages(id, { page: pageToLoad, size: pageSize, sort: 'createdAt,DESC' });
      const newMessages = res.data.content.reverse();
      if (newMessages.length < pageSize) setHasMore(false);
      setMessages(prev => (pageToLoad === 0 ? newMessages : [...newMessages, ...prev]));

      if (scrollToBottom && containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      } else if (containerRef.current && pageToLoad > 0) {
        const container = containerRef.current;
        container.scrollTop = container.scrollHeight / 2;
      }
    } catch (err) {
      console.error('Errore caricamento messaggi', err);
    }
  };

  useEffect(() => {
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [messages]);

  // --- Invio messaggio ---
  const sendMessage = async () => {
    if (!newMessage.trim()) {
      setShowSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append(
        'data',
        new Blob([JSON.stringify({ content: newMessage })], { type: 'application/json' })
      );

      selectedFiles.forEach(file => {
        formData.append("files", file);
      });

      const res = await chatApi.sendMessage(id, formData);
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      setSelectedFiles([]);
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
      fetchMessages(0,true);
    } catch (err) {
      console.error('Errore invio messaggio', err);
    } finally {
      setLoading(false);
    }
  };

  // --- Infinite scroll verso l'alto ---
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container || !hasMore) return;
    if (container.scrollTop < 50) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchMessages(nextPage);
    }
  };

  const handleDownload = async (attachment) => {
    try {
      setLoading(true);
      const response = await chatApi.downloadAttachment(attachment.id);
      const type = attachment.contentType || 'application/octet-stream';
      const blob = new Blob([response.data], { type });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.fileName || 'file');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!room) return null;
  if (loading) return <CircularProgress />;

  return (
    <Container>
      <BackButtonDesktop />
      <Typography variant="h5" sx={{ mb: 2 }}>
        {room.name}
      </Typography>

      <Stack
        spacing={1}
        sx={{ maxHeight: 500, overflowY: 'auto', mb: 2 }}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {messages.map((m, idx) => (
          <Box
            key={`${m.id}-${idx}`}
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: m.authorId === user.id ? '#DCF8C6' : '#F1F0F0'
            }}
          >
            <Typography variant="body2">{m.senderRole}</Typography>
            <Typography variant="body2">{m.senderName}</Typography>
            <Typography variant="body2">{m.content}</Typography>
            <Typography variant="caption">{new Date(m.createdAt).toLocaleString()}</Typography>
            {m.attachments && m.attachments.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {m.attachments.map((a) => (
                  <Button
                    key={a.id}
                    size="small"
                    onClick={() => handleDownload(a)}
                  >
                    {a.fileName}
                  </Button>
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      {/* UPLOAD FILE MULTIPLI */}
      <Stack direction="row" spacing={1}>
        <input
          type="file"
          multiple
          style={{ display: 'none' }}
          id="chat-file-upload"
          onChange={(e) => {
            const newFiles = Array.from(e.target.files);
            setSelectedFiles(prev => [...prev, ...newFiles]); // Accoda i file
          }}
        />
        <label htmlFor="chat-file-upload">
          <Button variant="outlined" component="span">
            {selectedFiles.length > 0
              ? `${selectedFiles.length} file selezionati`
              : 'Carica file'}
          </Button>
        </label>
        {selectedFiles.length > 0 && (
          <Stack spacing={0.5} sx={{ ml: 1 }}>
            {selectedFiles.map((file, index) => (
                <Stack key={index} direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption">{file.name}</Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                    }}
                  >
                    ✕
                  </Button>
                </Stack>
            ))}
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Scrivi un messaggio..."
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
        />
        <Button variant="contained" onClick={sendMessage}>Invia</Button>
      </Stack>

      {/* SNACKBAR per messaggio vuoto */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" sx={{ width: '100%' }} onClose={() => setShowSnackbar(false)}>
          Devi scrivere un messaggio prima di inviare!
        </Alert>
      </Snackbar>
    </Container>
  );
}
